import type { Category } from '@wix/answers-api';
import { Header, PageType as CommonPageType, ConsumerSource } from '@wix/cc-user-facing-common-components';
import { useHttpClient } from '@wix/fe-essentials-standalone';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';

import { useHeaderBI } from './bi';
import { useGoToSearchResultsPage } from '../../hooks/useGoToSearchResultsPage';
import { useIsGenAnswer } from '../../hooks/useIsGenAnswer';
import useIsHelpCenterCopilotEnabled from '../../hooks/useIsHelpCenterCopilotEnabled';
import useIsHireProfessionalInHeaderEnabled from '../../hooks/useIsHireProfessionalInHeaderEnabled';
import useIsKbServiceEnabled from '../../hooks/useIsKbServiceEnabled';
import { useIsSitePickerEnabled } from '../../hooks/useIsSitePickerEnabled';
import { useResponsive } from '../../hooks/useResponsive';
import { useWixRecorder } from '../../hooks/useWixRecorder';
import { PageType } from '../../types';
import { useStore } from '../../zustand/stores';
import { InnerLink } from '../InnerLink';
import { SiteSelector, SiteSelectorParent } from '../SiteSelector';

const SiteSelectorWrapper: React.FC<{
  itemId?: string;
  parent: string;
  parentElm: HTMLElement | null;
  relativeText: string;
}> = ({ itemId, parent, parentElm, relativeText }) => (
  <SiteSelector
    itemId={itemId}
    parent={parent as SiteSelectorParent}
    parentElm={parentElm}
    relativeText={relativeText}
  />
);

const HeaderWrapper: React.FC = () => {
  const categoriesTree = useStore(
    (state) => state.categoriesTreeSlice.categoriesTree
  );
  const { isWixStudioUser, user } = useStore((state) => state.userSlice);
  const pageType = useStore((state) => state.pageSlice.pageType);
  const itemId = useStore((state) => state.pageSlice.itemId);
  const siteSelectorData = useStore((state) => state.siteSelectorSlice);
  const { isMobile, isTablet } = useResponsive();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const { locale, asPath } = useRouter();
  const httpClient = useHttpClient();
  const [fallbackCategoriesTree, setFallbackCategoriesTree] = useState<Category[]>([]);
  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();
  const isGenAnswerEnabled = useIsGenAnswer();
  const isSitePickerEnabled = useIsSitePickerEnabled();
  const isHireProfessionalInHeaderEnabled = useIsHireProfessionalInHeaderEnabled();
  const isKbServiceEnabled = useIsKbServiceEnabled();
  const { goToSearchResultsPage } = useGoToSearchResultsPage();
  const { onLinkClick, onItemView } = useHeaderBI();
  useWixRecorder();

  useEffect(() => {
    const fetchCategories = async () => {
      if (!categoriesTree || categoriesTree.length === 0) {
        try {
          const { data: categories } = await httpClient.get<Category[]>(
            '/api/category/tree',
            { params: { locale: locale || 'en' } }
          );
          setFallbackCategoriesTree(categories);
        } catch (error) {
          console.error('Failed to fetch categories tree:', error);
          setFallbackCategoriesTree([]);
        }
      }
    };

    void fetchCategories();
  }, [categoriesTree, httpClient, locale]);

  const effectiveCategoriesTree =
    categoriesTree && categoriesTree.length > 0
      ? categoriesTree
      : fallbackCategoriesTree;

  const handleGoToSearchResultsPage = useCallback(
    async (params: { term: string; previousPageType: CommonPageType; overrideTerm?: string }) => {
      await goToSearchResultsPage({
        term: params.term,
        previousPageType: params.previousPageType as unknown as PageType,
        overrideTerm: params.overrideTerm,
        searchSource: 'header',
      });
    },
    [goToSearchResultsPage]
  );

  return (
    <Header
      locale={locale || 'en'}
      user={user}
      isWixStudioUser={isWixStudioUser}
      pageType={pageType}
      categoriesTree={effectiveCategoriesTree}
      isHelpCenterCopilotEnabled={isHelpCenterCopilotEnabled}
      isGenAnswerEnabled={isGenAnswerEnabled}
      isSitePickerEnabled={isSitePickerEnabled}
      showHireProfessionalInHeader={isHireProfessionalInHeaderEnabled}
      showCategoryDescriptions={isKbServiceEnabled}
      consumerSource={ConsumerSource.HELP_CENTER}
      siteSelectorData={siteSelectorData}
      itemId={itemId}
      showSearchModal={showSearchModal}
      setShowSearchModal={setShowSearchModal}
      biCallbacks={{ onLinkClick, onItemView }}
      goToSearchResultsPage={handleGoToSearchResultsPage}
      ssrDeviceHint={{ isMobile, isTablet }}
      currentPath={asPath}
      InnerLinkComponent={InnerLink}
      SiteSelectorComponent={SiteSelectorWrapper}
    />
  );
};

export default HeaderWrapper;
